import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { reportAPI } from '../../services/api';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { useSystemTotal } from '../../hooks/useSystemTotal';
import { brandColors, brandCss } from '../../theme/appTheme';
import { useAppTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { getChartColors } from '../../constants/dashboardModules';
import { useLocale } from '../../contexts/LocaleContext';
import { tableContainerSx, tableHeadRowSx, tableHeadCellSx, tableBodyCellSx } from '../../theme/tableStyles';

const DATE_PRESETS = [
  { id: '7d', label: '7 days', days: 7 },
  { id: '30d', label: '30 days', days: 30 },
  { id: '90d', label: '90 days', days: 90 },
  { id: '365d', label: '1 year', days: 365 },
];

type FilterOption = { id: string; label: string; code?: string };
type ReportItem = {
  key: string;
  _id: string;
  name: string;
  description?: string;
  moduleKey: string;
  chartType?: string;
  groupBy?: string;
  type?: 'platform' | 'saved';
};

const REPORT_CREATE_MODULES: { key: string; label: string; groupBy: string[] }[] = [
  { key: 'trips', label: 'Trips', groupBy: ['status', 'branchLabel', 'clientLabel'] },
  { key: 'invoices', label: 'Invoices', groupBy: ['paymentStatus', 'clientLabel'] },
  { key: 'bilty', label: 'Bilty', groupBy: ['status', 'clientLabel'] },
  { key: 'clients', label: 'Clients', groupBy: ['status'] },
  { key: 'vehicles', label: 'Vehicles', groupBy: ['status'] },
  { key: 'drivers', label: 'Drivers', groupBy: ['status'] },
  { key: 'fuel', label: 'Fuel', groupBy: ['fuelStation'] },
];

function defaultDateRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 365);
  return {
    fromDate: from.toISOString().slice(0, 10),
    toDate: to.toISOString().slice(0, 10),
  };
}

function buildFilterParams(filters: {
  fromDate: string;
  toDate: string;
  branchId: string;
  clientId: string;
  status: string;
  vehicleId: string;
  driverId: string;
}) {
  const params: Record<string, string> = {
    fromDate: filters.fromDate,
    toDate: filters.toDate,
  };
  if (filters.branchId) params.branchId = filters.branchId;
  if (filters.clientId) params.clientId = filters.clientId;
  if (filters.status && filters.status !== 'all') params.status = filters.status;
  if (filters.vehicleId) params.vehicleId = filters.vehicleId;
  if (filters.driverId) params.driverId = filters.driverId;
  return params;
}

function exportCsv(rows: Record<string, unknown>[], filename: string) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const header = keys.join(',');
  const body = rows.map((row) =>
    keys.map((k) => {
      const val = row[k];
      const str = val == null ? '' : String(val);
      return str.includes(',') ? `"${str.replace(/"/g, '""')}"` : str;
    }).join(',')
  ).join('\n');
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const TABLE_COLUMNS: Record<string, { key: string; label: string; type?: 'currency' | 'date' }[]> = {
  trips: [
    { key: 'number', label: 'Trip #' },
    { key: 'client', label: 'Client' },
    { key: 'branch', label: 'Branch' },
    { key: 'route', label: 'Route' },
    { key: 'status', label: 'Status' },
    { key: 'date', label: 'Date', type: 'date' },
  ],
  invoices: [
    { key: 'number', label: 'Invoice #' },
    { key: 'client', label: 'Client' },
    { key: 'amount', label: 'Amount', type: 'currency' },
    { key: 'status', label: 'Payment' },
    { key: 'date', label: 'Date', type: 'date' },
  ],
  bilty: [
    { key: 'number', label: 'Bilty #' },
    { key: 'client', label: 'Client' },
    { key: 'route', label: 'Route' },
    { key: 'amount', label: 'Amount', type: 'currency' },
    { key: 'status', label: 'Status' },
    { key: 'date', label: 'Date', type: 'date' },
  ],
  clients: [
    { key: 'name', label: 'Client' },
    { key: 'contact', label: 'Contact' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'status', label: 'Status' },
  ],
  drivers: [
    { key: 'name', label: 'Driver' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'status', label: 'Status' },
  ],
  fuel: [
    { key: 'station', label: 'Station' },
    { key: 'quantity', label: 'Quantity' },
    { key: 'amount', label: 'Amount', type: 'currency' },
    { key: 'date', label: 'Date', type: 'date' },
  ],
  default: [
    { key: 'label', label: 'Record' },
    { key: 'status', label: 'Status' },
    { key: 'date', label: 'Date', type: 'date' },
  ],
};

export const ReportsPage: React.FC = () => {
  const { formatCurrency, formatDate } = useLocale();
  const { hasPermission } = useAuth();
  const { brand } = useAppTheme();
  const { total: systemTotal, refresh: refreshTotal } = useSystemTotal('reports');
  const canViewReports = hasPermission('reports', 'view');
  const canCreateReports = hasPermission('reports', 'create');
  const chartColors = useMemo(
    () => getChartColors(brand.primary, brand.primaryDark, brand.primaryLight),
    [brand.primary, brand.primaryDark, brand.primaryLight],
  );
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [overview, setOverview] = useState<any>(null);
  const [filterOptions, setFilterOptions] = useState<{
    branches: FilterOption[];
    clients: FilterOption[];
    vehicles: FilterOption[];
    drivers: FilterOption[];
    statuses: string[];
  }>({ branches: [], clients: [], vehicles: [], drivers: [], statuses: [] });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<any>(null);
  const [datePreset, setDatePreset] = useState('365d');
  const [{ fromDate, toDate }, setDateRange] = useState(defaultDateRange);
  const [selectedReportKey, setSelectedReportKey] = useState('');
  const [reportParams, setReportParams] = useState(() => {
    const range = defaultDateRange();
    return buildFilterParams({
      ...range,
      branchId: '',
      clientId: '',
      status: 'all',
      vehicleId: '',
      driverId: '',
    });
  });
  const [branchId, setBranchId] = useState('');
  const [clientId, setClientId] = useState('');
  const [status, setStatus] = useState('all');
  const [vehicleId, setVehicleId] = useState('');
  const [driverId, setDriverId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    moduleKey: 'trips',
    groupBy: 'status',
    chartType: 'bar',
    description: '',
  });

  const createModuleDef = useMemo(
    () => REPORT_CREATE_MODULES.find((m) => m.key === createForm.moduleKey) || REPORT_CREATE_MODULES[0],
    [createForm.moduleKey]
  );

  const loadReports = useCallback(async () => {
    try {
      const res = await reportAPI.list();
      const list: ReportItem[] = res.data || [];
      setReports(list);
      setSelectedReportKey((prev) => {
        if (prev && list.some((r) => r.key === prev)) return prev;
        return list[0]?.key || '';
      });
      refreshTotal();
      return list;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || 'Failed to load report list');
      return [];
    }
  }, [refreshTotal]);

  const selectedReport = useMemo(
    () => reports.find((r) => r.key === selectedReportKey) || reports[0] || null,
    [reports, selectedReportKey]
  );

  const filterParams = useMemo(
    () => buildFilterParams({ fromDate, toDate, branchId, clientId, status, vehicleId, driverId }),
    [fromDate, toDate, branchId, clientId, status, vehicleId, driverId]
  );

  const activeFiltersCount = useMemo(() => {
    let n = 0;
    if (branchId) n += 1;
    if (clientId) n += 1;
    if (status !== 'all') n += 1;
    if (vehicleId) n += 1;
    if (driverId) n += 1;
    return n;
  }, [branchId, clientId, status, vehicleId, driverId]);

  const loadFilters = useCallback(async (moduleKey: string) => {
    try {
      const res = await reportAPI.filters({ moduleKey });
      setFilterOptions({
        branches: (res.data.branches || []).map((b: { id: string; label: string; code?: string }) => ({
          id: b.id, label: b.label, code: b.code,
        })),
        clients: (res.data.clients || []).map((c: { id: string; label: string }) => ({ id: c.id, label: c.label })),
        vehicles: (res.data.vehicles || []).map((v: { id: string; label: string }) => ({ id: v.id, label: v.label })),
        drivers: (res.data.drivers || []).map((d: { id: string; label: string }) => ({ id: d.id, label: d.label })),
        statuses: res.data.statuses || [],
      });
    } catch {
      /* keep previous options */
    }
  }, []);

  const loadOverview = useCallback(async (params = filterParams) => {
    setLoading(true);
    setError('');
    try {
      const overviewRes = await reportAPI.overview(params);
      setOverview(overviewRes.data);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || 'Failed to load reports. Ensure backend is running: cd server && npm start');
    } finally {
      setLoading(false);
    }
  }, [filterParams]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleCreateReport = async () => {
    if (!createForm.name.trim()) {
      setError('Report name is required');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const res = await reportAPI.create(createForm);
      await loadReports();
      setSelectedReportKey(res.data.key);
      setCreateOpen(false);
      setCreateForm({ name: '', moduleKey: 'trips', groupBy: 'status', chartType: 'bar', description: '' });
    } catch {
      setError('Failed to create report');
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const applyFilters = useCallback(() => {
    const next = buildFilterParams({ fromDate, toDate, branchId, clientId, status, vehicleId, driverId });
    setReportParams(next);
    loadOverview(next);
  }, [fromDate, toDate, branchId, clientId, status, vehicleId, driverId, loadOverview]);

  useEffect(() => {
    if (selectedReport?.moduleKey) {
      loadFilters(selectedReport.moduleKey);
    }
  }, [selectedReport?.moduleKey, loadFilters]);

  const handlePreset = (id: string, days: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    const range = {
      fromDate: from.toISOString().slice(0, 10),
      toDate: to.toISOString().slice(0, 10),
    };
    setDatePreset(id);
    setDateRange(range);
    const next = buildFilterParams({ ...range, branchId, clientId, status, vehicleId, driverId });
    setReportParams(next);
    loadOverview(next);
  };

  const clearFilters = () => {
    setBranchId('');
    setClientId('');
    setStatus('all');
    setVehicleId('');
    setDriverId('');
    const range = defaultDateRange();
    setDateRange(range);
    setDatePreset('365d');
    const next = buildFilterParams({ ...range, branchId: '', clientId: '', status: 'all', vehicleId: '', driverId: '' });
    setReportParams(next);
    loadOverview(next);
  };

  const handleGenerate = useCallback(async (params = reportParams, reportKey = selectedReportKey) => {
    const key = reportKey || selectedReport?.key;
    if (!key) {
      setError('No report templates available. Refresh the page — templates are created automatically on first load.');
      return;
    }
    if (!canViewReports) {
      setError('You do not have permission to run reports. Ask your admin to enable Reports → View.');
      return;
    }
    setRunning(true);
    setError('');
    try {
      const res = await reportAPI.run(key, params);
      setResult(res.data);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string }; status?: number } };
      const msg = e.response?.data?.message;
      if (e.response?.status === 403) {
        setError(msg || 'Permission denied for reports');
      } else if (e.response?.status === 404) {
        setError(msg || 'Report not found. Select another report or refresh the list.');
      } else {
        setError(msg || 'Failed to generate report. Ensure the server is running on port 5001.');
      }
    } finally {
      setRunning(false);
    }
  }, [reportParams, selectedReportKey, selectedReport, canViewReports]);

  useEffect(() => {
    if (!selectedReportKey || !canViewReports) return;
    handleGenerate(reportParams, selectedReportKey);
  }, [selectedReportKey, reportParams, canViewReports, handleGenerate]);

  const chartType = result?.report?.chartType || 'bar';
  const chartData = result?.chartData?.length ? result.chartData : [{ name: 'No data', count: 0 }];
  const tableRows: Record<string, unknown>[] = result?.tableRows || [];
  const tableCols = TABLE_COLUMNS[result?.report?.moduleKey || ''] || TABLE_COLUMNS.default;
  const hasAggregation = Boolean(
    result?.report?.moduleKey === 'invoices'
    || result?.chartData?.some((c: { total?: number }) => (c.total ?? 0) > 0)
  );

  const formatCell = (col: { key: string; type?: string }, row: Record<string, unknown>) => {
    const val = row[col.key];
    if (col.type === 'currency' && val != null) return formatCurrency(Number(val));
    if (col.type === 'date' && val) return formatDate(String(val));
    return val != null && val !== '' ? String(val) : '—';
  };

  return (
    <Box>
      <PageHeader
        title="Reports"
        subtitle={getPageSubtitle('reports', 'Reports', { total: systemTotal })}
        icon={<AssessmentIcon />}

        action={
          canCreateReports ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
              sx={{ background: brandCss.gradientPrimary, borderRadius: 2, fontWeight: 700 }}>
              Create Report
            </Button>
          ) : undefined
        }
      />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>Create Custom Report</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Report Name" size="small" value={createForm.name}
            onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))} fullWidth />
          <FormControl size="small" fullWidth>
            <InputLabel>Module</InputLabel>
            <Select label="Module" value={createForm.moduleKey}
              onChange={(e) => {
                const mod = REPORT_CREATE_MODULES.find((m) => m.key === e.target.value);
                setCreateForm((f) => ({ ...f, moduleKey: e.target.value, groupBy: mod?.groupBy[0] || 'status' }));
              }}>
              {REPORT_CREATE_MODULES.map((m) => (
                <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Group By</InputLabel>
            <Select label="Group By" value={createForm.groupBy}
              onChange={(e) => setCreateForm((f) => ({ ...f, groupBy: e.target.value }))}>
              {createModuleDef.groupBy.map((g) => (
                <MenuItem key={g} value={g}>{g.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Chart Type</InputLabel>
            <Select label="Chart Type" value={createForm.chartType}
              onChange={(e) => setCreateForm((f) => ({ ...f, chartType: e.target.value }))}>
              <MenuItem value="bar">Bar</MenuItem>
              <MenuItem value="pie">Pie</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Description (optional)" size="small" multiline rows={2} value={createForm.description}
            onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))} fullWidth />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateReport} disabled={creating}
            sx={{ background: brandCss.gradientPrimary }}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: 8,
          zIndex: 20,
          mb: 2,
          p: 1.5,
          borderRadius: 2,
          border: `1px solid ${brandColors.border}`,
          bgcolor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 4px 24px rgba(15,23,42,0.08)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, flexWrap: 'wrap' }}>
          <FilterListIcon sx={{ fontSize: 18, color: brandCss.primary }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 13 }}>
            Report filters
          </Typography>
          {activeFiltersCount > 0 && (
            <Chip label={`${activeFiltersCount} active`} size="small" color="primary" sx={{ height: 20, fontSize: 10.5 }} />
          )}
          <Box sx={{ flex: 1 }} />
          <Button size="small" startIcon={<ClearIcon sx={{ fontSize: 16 }} />} onClick={clearFilters} sx={{ fontSize: 12 }}>
            Reset
          </Button>
          <Button size="small" variant="outlined" onClick={applyFilters} disabled={loading} sx={{ fontSize: 12 }}>
            Apply filters
          </Button>
          <Tooltip title="Refresh overview">
            <IconButton size="small" onClick={() => loadOverview()} disabled={loading}>
              {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.25 }}>
          {DATE_PRESETS.map((p) => (
            <Chip
              key={p.id}
              label={p.label}
              size="small"
              clickable
              onClick={() => handlePreset(p.id, p.days)}
              sx={{
                height: 26,
                fontSize: 11.5,
                fontWeight: datePreset === p.id ? 700 : 500,
                bgcolor: datePreset === p.id ? brand.primary : '#f1f5f9',
                color: datePreset === p.id ? '#fff' : brandColors.textDark,
              }}
            />
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.25, mb: 1.25 }}>
          <TextField
            label="From date"
            type="date"
            size="small"
            value={fromDate}
            onChange={(e) => { setDateRange((d) => ({ ...d, fromDate: e.target.value })); setDatePreset('custom'); }}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiInputBase-root': { fontSize: 12 } }}
          />
          <TextField
            label="To date"
            type="date"
            size="small"
            value={toDate}
            onChange={(e) => { setDateRange((d) => ({ ...d, toDate: e.target.value })); setDatePreset('custom'); }}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiInputBase-root': { fontSize: 12 } }}
          />
          <FormControl size="small">
            <InputLabel sx={{ fontSize: 12 }}>Report</InputLabel>
            <Select
              label="Report"
              value={selectedReportKey || (reports[0]?.key ?? '')}
              onChange={(e) => { setSelectedReportKey(e.target.value); }}
              sx={{ fontSize: 12 }}
            >
              {reports.length === 0 && <MenuItem value="">No reports available</MenuItem>}
              {reports.map((r) => (
                <MenuItem key={r.key} value={r.key}>
                  {r.name}{r.type === 'saved' ? ' (Custom)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel sx={{ fontSize: 12 }}>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ fontSize: 12 }}>
              <MenuItem value="all">All statuses</MenuItem>
              {filterOptions.statuses.map((s) => (
                <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel sx={{ fontSize: 12 }}>Branch</InputLabel>
            <Select label="Branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} sx={{ fontSize: 12 }}>
              <MenuItem value="">All branches</MenuItem>
              {filterOptions.branches.map((b) => (
                <MenuItem key={b.id} value={b.id}>{b.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel sx={{ fontSize: 12 }}>Client</InputLabel>
            <Select label="Client" value={clientId} onChange={(e) => setClientId(e.target.value)} sx={{ fontSize: 12 }}>
              <MenuItem value="">All clients</MenuItem>
              {filterOptions.clients.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel sx={{ fontSize: 12 }}>Vehicle</InputLabel>
            <Select label="Vehicle" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} sx={{ fontSize: 12 }}>
              <MenuItem value="">All vehicles</MenuItem>
              {filterOptions.vehicles.map((v) => (
                <MenuItem key={v.id} value={v.id}>{v.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel sx={{ fontSize: 12 }}>Driver</InputLabel>
            <Select label="Driver" value={driverId} onChange={(e) => setDriverId(e.target.value)} sx={{ fontSize: 12 }}>
              <MenuItem value="">All drivers</MenuItem>
              {filterOptions.drivers.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            onClick={() => handleGenerate()}
            disabled={running || !reports.length}
            sx={{ background: brandCss.gradientPrimary, borderRadius: 2, fontWeight: 700 }}
          >
            Generate Report
          </Button>
          {selectedReport && (
            <Typography variant="caption" color="text.secondary">
              {selectedReport.description || `${selectedReport.moduleKey} · grouped by ${selectedReport.groupBy}`}
            </Typography>
          )}
        </Box>
      </Paper>

      {loading && !overview ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: brandCss.primary }} />
        </Box>
      ) : (
        <>
          {overview?.stats && (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
              gap: 1.5,
              mb: 2,
            }}>
              {[
                { label: 'Trips', value: overview.stats.trips },
                { label: 'Clients', value: overview.stats.clients },
                { label: 'Vehicles', value: overview.stats.vehicles },
                { label: 'Invoices', value: overview.stats.invoices },
                { label: 'Bilty', value: overview.stats.bilty },
                { label: 'Revenue', value: formatCurrency(overview.stats.revenue || 0) },
              ].map((s) => (
                <Card key={s.label} sx={{ textAlign: 'center', py: 0.5 }}>
                  <CardContent sx={{ py: '10px !important' }}>
                    <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ color: brandCss.primaryDark }}>
                      {s.value}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {overview && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
              {[
                { title: 'Trip Status', data: overview.tripStatus, type: 'pie' as const },
                { title: 'Invoice Payments', data: overview.invoiceStatus, type: 'bar' as const },
                { title: 'Bilty Summary', data: overview.biltyStatus, type: 'bar' as const },
              ].map((chart) => (
                <Card key={chart.title}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>{chart.title}</Typography>
                    <ResponsiveContainer width="100%" height={180}>
                      {chart.type === 'pie' ? (
                        <PieChart>
                          <Pie
                            data={chart.data?.length ? chart.data : [{ name: 'No data', count: 1 }]}
                            dataKey="count"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={65}
                          >
                            {(chart.data?.length ? chart.data : [{ name: 'No data', count: 1 }]).map((_: unknown, i: number) => (
                              <Cell key={i} fill={chartColors[i % chartColors.length]} />
                            ))}
                          </Pie>
                          <ReTooltip />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                        </PieChart>
                      ) : (
                        <BarChart data={chart.data?.length ? chart.data : [{ name: 'No data', count: 0 }]}>
                          <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <ReTooltip />
                          <Bar dataKey="count" fill={brand.primary} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {reports.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              No report templates loaded yet. Click <strong>Apply filters</strong> to refresh, or restart the server.
              Templates are auto-created when you open this page.
            </Alert>
          )}

          {result && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Typography variant="h6" fontWeight={700}>
                    {result.report?.name}
                  </Typography>
                  <Chip label={`${result.totalRecords} records`} size="small" color="primary" />
                  <Chip label={`${reportParams.fromDate} → ${reportParams.toDate}`} size="small" variant="outlined" />
                  {result.totalRecords === 0 && (
                    <Chip label="No data in date range" size="small" color="warning" variant="outlined" />
                  )}
                  <Box sx={{ flex: 1 }} />
                  {(tableRows.length > 0 || (result.rows?.length > 0)) && (
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => exportCsv(
                        tableRows.length > 0 ? tableRows : (result.rows as Record<string, unknown>[]),
                        `${result.report?.key || 'report'}.csv`
                      )}
                    >
                      Export CSV
                    </Button>
                  )}
                </Box>

                <ResponsiveContainer width="100%" height={280}>
                  {chartType === 'pie' ? (
                    <PieChart>
                      <Pie data={chartData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90}>
                        {chartData.map((_: unknown, i: number) => (
                          <Cell key={i} fill={chartColors[i % chartColors.length]} />
                        ))}
                      </Pie>
                      <ReTooltip />
                      <Legend />
                    </PieChart>
                  ) : (
                    <BarChart data={chartData}>
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <ReTooltip />
                      <Bar dataKey="count" fill={brandCss.primary} radius={[4, 4, 0, 0]} />
                      {hasAggregation && (
                        <Bar dataKey="total" fill={brand.primaryDark} radius={[4, 4, 0, 0]} name="Total" />
                      )}
                    </BarChart>
                  )}
                </ResponsiveContainer>

                {tableRows.length > 0 && (
                  <TableContainer sx={{ ...tableContainerSx, mt: 2, maxHeight: 360 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={tableHeadRowSx}>
                          {tableCols.map((col) => (
                            <TableCell key={col.key} sx={tableHeadCellSx()}>{col.label}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tableRows.map((row) => (
                          <TableRow key={String(row.id)} hover>
                            {tableCols.map((col) => (
                              <TableCell key={col.key} sx={tableBodyCellSx}>
                                {formatCell(col, row)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};
